import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import EnhetsgrupperingApi from 'services/KjederegisteretAdminApi/EnhetsgrupperingApi';
import DropdownTreeSelect, { Mode, TreeNode } from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { IEnhetsgruppeNode } from './types';
import CommonLoader from 'components/CommonLoader';
import { EnhetsgruppeTreHelper } from './EnhetsgruppeTreHelper';
import { EnhetsgruppeTreeItem } from 'services/KjederegisteretAdminApi/EnhetsgrupperingApi/types';

// Skjul noder uten treff (fasett-count 0) med mindre de er valgt eller har et barn som skal beholdes.
const keepNodeWithFacets = (node: IEnhetsgruppeNode, facetCounts: Map<number, number>): boolean => {
  node.children = (node.children || []).filter((child) => keepNodeWithFacets(child, facetCounts));
  const count = facetCounts.get(node.nr) ?? 0;
  return count > 0 || node.checked || node.children.length > 0;
};

interface Props {
  treeSelectMode?: Mode;

  /**
   * Custom text for placeholder and no matches found.
   * If not provided, default texts will be used.
   */
  customTexts?: { placeholder: string; noMatches: string };
  disabled?: boolean;

  /**
   * Top nodes are not selectable.
   */
  disableTopNodes?: boolean;

  /**
   * Function that returns an array of pre-selected enhetsgruppenr.
   * @param pickerType
   * @returns
   */
  findPreselectedNodesByNumber?: (pickerType: string) => number[];

  /**
   * Function that returns an array of pre-selected enhetsgruppe IDs.
   * @param pickerType
   * @returns
   */
  findPreselectedNodesByID?: (pickerType: string) => number[];

  /**
   * Called when the selection changes.
   * @param grupperinger
   * @returns
   */
  onSelectionChanged: (grupperinger: IEnhetsgruppeNode[]) => void;

  /**
   * If true, the picker will close when a selection is made.
   */
  closeOnSelect?: boolean;

  /**
   * Allows filtering of enhetsgruppering types. IS or OI
   */
  enhetgrupperingType?: string;

  /**
   * If true, only top-level enhetsgrupperinger will be loaded.
   */
  onlyTopLevel?: boolean;

  /**
   * Når satt vises antall treff (fasett) som suffiks på hver node i treet.
   */
  facetCounts?: Map<number, number>;

  /**
   * Når satt (sammen med facetCounts) skjules uvalgte noder med 0 treff.
   */
  hideEmptyFacets?: boolean;
}

const GrupperingPicker = (props: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isMountedRef = useIsMountedRef();
  const pickerRef = useRef<DropdownTreeSelect>(null);

  const defTexts = {
    placeholder: 'Velg...',
    noMatches: 'Ingen treff',
  };

  // Find out if any nodes are pre-selected by ID or number
  const [preSelectedNodeNumbers, setPreSelectedNodeNumbers] = useState<number[]>([]);
  const [preSelectedNodeIDS, setPreSelectedNodeIDS] = useState<number[]>([]);

  useEffect(() => {
    if (isMountedRef.current && props.findPreselectedNodesByID) {
      const res = props.findPreselectedNodesByID('gruppering');
      setPreSelectedNodeIDS(res || []);
    }
  }, [props.findPreselectedNodesByID]);

  useEffect(() => {
    if (isMountedRef.current && props.findPreselectedNodesByNumber) {
      const res = props.findPreselectedNodesByNumber('gruppering');
      console.log('Pre-selected node numbers:', res);
      setPreSelectedNodeNumbers(res || []);
    }
  }, [props.findPreselectedNodesByNumber]);

  const [enhetsgruppeTre, setEnhetsgruppeTre] = useState<EnhetsgruppeTreeItem[]>([]);

  // load enhetsgrupperinger from API
  const loadGrupperinger = useCallback(async () => {
    console.log('Henter enhetsgrupperinger fra API');
    setIsLoading(true);
    const dataFraAPI = await EnhetsgrupperingApi.getEnhetsgrupperingerTree(
      false,
      props.enhetgrupperingType,
      props.onlyTopLevel,
    );
    setEnhetsgruppeTre(dataFraAPI || []);
    setIsLoading(false);
  }, [props.enhetgrupperingType, props.onlyTopLevel]);

  // And memoize handleChange to prevent recreations
  const handleChange = useCallback(
    (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
      if (Array.isArray(selectedNodes)) {
        props.onSelectionChanged(
          selectedNodes.map((x) =>
            EnhetsgruppeTreHelper.treeNodeToEnhetsgruppeNode(
              x,
              preSelectedNodeIDS,
              preSelectedNodeNumbers,
              props.disableTopNodes,
            ),
          ),
        );
        if (isMountedRef.current && props.closeOnSelect && pickerRef.current?.state.showDropdown) {
          pickerRef.current.searchInput.click();
        }
      }
    },
    [
      props.onSelectionChanged,
      preSelectedNodeIDS,
      preSelectedNodeNumbers,
      props.disableTopNodes,
      props.closeOnSelect,
      isMountedRef,
    ],
  );

  // Update data with pre-selected nodes if they exist
  const data = useMemo(() => {
    if (!enhetsgruppeTre.length) return [];

    const mapped = enhetsgruppeTre.map((item) =>
      EnhetsgruppeTreHelper.mapToEnhetsgruppeNode(
        item,
        preSelectedNodeIDS,
        preSelectedNodeNumbers,
        props.disableTopNodes,
        undefined,
        props.facetCounts,
      ),
    );

    if (!props.facetCounts || !props.hideEmptyFacets) return mapped;
    return mapped.filter((node) => keepNodeWithFacets(node, props.facetCounts));
  }, [
    enhetsgruppeTre,
    preSelectedNodeIDS,
    preSelectedNodeNumbers,
    props.disableTopNodes,
    props.facetCounts,
    props.hideEmptyFacets,
  ]);

  // last alle enhetsgrupperinger
  useEffect(() => {
    loadGrupperinger();
  }, [loadGrupperinger]);

  return (
    <>
      <DropdownTreeSelect
        className="reactDropdownMultiPicker"
        mode={props.treeSelectMode}
        texts={props.customTexts || defTexts}
        data={data}
        keepTreeOnSearch
        keepChildrenOnSearch
        showPartiallySelected
        keepOpenOnSelect
        onChange={handleChange}
        disabled={props.disabled || false}
        ref={pickerRef}
      />
      {isLoading && (
        <div>
          <CommonLoader />
          <span> Henter data... </span>
        </div>
      )}
    </>
  );
};

// Fix the React.memo comparison
export default React.memo(GrupperingPicker, (prevProps, nextProps) => {
  // Compare primitive props
  const primitiveProps = [
    'treeSelectMode',
    'disabled',
    'disableTopNodes',
    'closeOnSelect',
    'enhetgrupperingType',
    'onlyTopLevel',
    'hideEmptyFacets',
  ];

  for (const prop of primitiveProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props changed, re-render needed
    }
  }

  // Compare customTexts object
  if (JSON.stringify(prevProps.customTexts) !== JSON.stringify(nextProps.customTexts)) {
    return false;
  }

  // Re-render when facet counts change (new reference per search)
  if (prevProps.facetCounts !== nextProps.facetCounts) {
    return false;
  }

  // For function props, we assume they're stable if other props haven't changed
  // The parent should memoize these functions with useCallback
  return true; // Props are equal, no re-render needed
});
